// @flow
import React, { useContext, useEffect, useReducer, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { isEqual, omit, pick } from 'lodash';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger.jsx';
import { OPEN_CRITERIA_WHO_EVENT } from 'Components/widgets/criteria/criteria-who/constants';
import { FilterWho } from 'Components/widgets/criteria/criteria-who/filter-who/FilterWho.jsx';
import {
  IS_INVALID,
  IS_VALID,
  NOT_VALIDATED,
} from 'Components/frameworks/form-validator-hoc/validationStates';
import { t } from 'Src/i18n';
import { changeKidsNumber, setKidsValidationState } from 'Store/faceting/facetingSearch.action';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { getAdultsNumber, getKids } from 'Store/trip-information/storeHelper';
import {
  getValidationStateFromKid,
  getValidationStateFromKids,
  hasOnlyValidKids,
} from 'Utils/faceting/kidsValidationStateUtils';
import { useTracking } from 'Tracking/tracking-api/useTracking';

type Props = {
  isKidBirthdateRequired?: boolean,
  isJoyview?: boolean,
};

const KIDS_ACTIONS = {
  CHANGE_KID: 'CHANGE_KID',
  CHANGE_KIDS_COUNT: 'CHANGE_KIDS_COUNT',
  REMOVE_KIDS: 'REMOVE_KIDS',
  SYNC_KIDS: 'SYNC_KIDS',
};

const getKidBirthdateAndChildcare = kid => pick(kid, ['birthdate', 'childcareIncluded']);
const getKidWithoutStatus = kid => omit(kid, ['status']);
const getKidWithStatus = kid => ({ ...kid, status: getValidationStateFromKid(kid) });

export const CriteriaWho = (props: Props) => {
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const dispatch = useDispatch();
  const adultsNumber = useSelector(getAdultsNumber);
  const kids = useSelector(getKids);
  const track = useTracking({ actionGA: criteriaContext.trackingActionName });

  const [inputAdultsNumber, setInputAdultsNumber] = useState(adultsNumber || DEFAULT_ADULTS_NB);
  const [inputKids, setInputKids] = useReducer((state, action) => {
    switch (action.type) {
      case KIDS_ACTIONS.SYNC_KIDS:
        return action.payload;
      case KIDS_ACTIONS.REMOVE_KIDS:
        return [];
      case KIDS_ACTIONS.CHANGE_KIDS_COUNT:
        return action.payload > state.length
          ? [
              ...state,
              {
                birthdate: undefined,
                childcareIncluded: true,
                neolid: undefined,
                status: NOT_VALIDATED,
              },
            ]
          : state.slice(0, -1);
      case KIDS_ACTIONS.CHANGE_KID:
        return state.map(
          (kid, index) => (index === action.payload.index ? action.payload.kid : kid),
        );
      default:
        return state;
    }
  }, kids.map(getKidWithStatus));

  const inputKidsValidationState = inputKids.map(kid => kid.status);
  const inputKidsEqualKids = isEqual(
    inputKids.map(getKidBirthdateAndChildcare),
    kids.map(getKidBirthdateAndChildcare),
  );
  const inputAdultsNumberEqualsAdultsNumber = inputAdultsNumber === adultsNumber;

  // Store update effect
  useEffect(
    () => {
      const adultsAreSet = adultsNumber > 0;
      const kidsAreSet = hasOnlyValidKids(getValidationStateFromKids(kids)); // Is also true when there are no kids
      setCriteriaContext({ criteriaWhoIsSet: adultsAreSet && kidsAreSet });
    },
    [adultsNumber, kids],
  );

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWho',
    });
  };

  // Open/Close effect
  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_WHO_EVENT, openCriteriaEventHandler);
    return () => document.removeEventListener(OPEN_CRITERIA_WHO_EVENT, openCriteriaEventHandler);
  }, []);

  const saveParticipants = () => {
    if (!inputKidsEqualKids) {
      dispatch(setKids(inputKids.map(getKidWithoutStatus)));
      dispatch(setKidsValidationState(inputKidsValidationState));
      if (kids.length !== inputKids.length) {
        dispatch(changeKidsNumber(inputKids.length));
      }
    } else {
      // Sync kids store with inputKids
      setInputKids({
        type: KIDS_ACTIONS.SYNC_KIDS,
        payload: inputKids,
      });
    }
    if (inputKids.length > 0 && inputAdultsNumber === 0) {
      dispatch(setAdultsNumber(DEFAULT_ADULTS_NB));
    } else if (!inputAdultsNumberEqualsAdultsNumber) {
      dispatch(setAdultsNumber(inputAdultsNumber));
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaWho',
    });
  };

  let labelParts = '';
  let labelPartsMobile = '';

  if (adultsNumber > 0) {
    const labelPartsTmp = [];
    const lengthPeople = adultsNumber + kids.length;
    if (lengthPeople) {
      labelPartsMobile = lengthPeople.toString(10);
      if (adultsNumber >= 1) {
        labelPartsTmp.push(t('booking.form.selectAdultsLabels', { count: adultsNumber }));
      }
      if (kids.length >= 1) {
        labelPartsTmp.push(t('booking.form.selectChildrenLabels', { count: kids.length }));
      }
      labelParts = labelPartsTmp.join(' - ');
    }
  } else {
    labelParts = t('faceting.who.title');
  }

  const buttonLabel = (
    <Fragment>
      <span
        className="hidden lg:flex flex-row py-0 flex-1 overflow-hidden items-center whitespace-no-wrap button-default"
        style={{ height: '1px' }}
      >
        {labelParts}
      </span>
      <span
        className={classnames(
          'lg:hidden flex flex-col py-1 justify-center overflow-hidden items-center whitespace-no-wrap button-default',
          { 'rtl:ml-2': !!labelPartsMobile },
        )}
        style={{ height: '1px' }}
      >
        {!!labelPartsMobile && <span>{labelPartsMobile}</span>}
      </span>
    </Fragment>
  );

  const trackKidsNumberChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: 'who_child',
        valueGA: value,
      });
    }
  };

  const trackAdultsNumberChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: 'who_adult',
        valueGA: value,
      });
    }
  };

  return (
    <CriteriaTrigger
      buttonLabel={buttonLabel}
      buttonIcon={ICONS.USERS}
      buttonIsFilled={criteriaContext.criteriaWhoIsSet}
      dropDownMinWidth={500}
      isOpen={criteriaDisplayContext.criteriaWhoIsOpen}
      isJoyview={props.isJoyview}
      trackingLabel="who"
      onOpen={() => {
        dispatchCriteriaDisplayContext({
          type: ACTIONS.OPEN_CRITERIA,
          payload: 'criteriaWho',
        });
      }}
      onClose={saveParticipants}
      onClear={() => {
        setCriteriaContext({ criteriaWhoIsSet: false });
        dispatch(setAdultsNumber(0));
        dispatch(setKids([]));
        dispatch(changeKidsNumber(0));
        dispatch(setKidsValidationState([]));
        setInputKids({ type: KIDS_ACTIONS.REMOVE_KIDS });
        setInputAdultsNumber(DEFAULT_ADULTS_NB);
      }}
      buttons={[
        {
          children: t('faceting.reset'),
          theme: 'link',
          onClick: () => {
            setInputAdultsNumber(DEFAULT_ADULTS_NB);
            setInputKids({ type: KIDS_ACTIONS.REMOVE_KIDS });
          },
          className: 'text-sm cursor-pointer',
          dataTestId: 'criteria-who-reset',
        },
        {
          children: t('faceting.validate'),
          colorBg: COLORS.TURQUOISE,
          dataTestId: 'criteria-who-validate',
          isDisabled: !hasOnlyValidKids(inputKidsValidationState),
          onClick: () => {
            saveParticipants();
            dispatchCriteriaDisplayContext({
              type: ACTIONS.OPEN_NEXT_CRITERIA,
              payload: { currentCriteria: 'criteriaWho', criteriaContext },
            });
          },
        },
      ]}
    >
      <FilterWho
        adultsNumber={inputAdultsNumber}
        kids={inputKids}
        placeholder={t('faceting.who.title')}
        label={t('faceting.who.subTitle')}
        adultsLabel={t('faceting.who.adults')}
        kidsLabel={t('faceting.who.kids')}
        kidsAgeLabel={t('faceting.who.ageSectionSubtitle')}
        kidsAgeLimitLabel={t('faceting.who.kidsAgeLimit')}
        dateFormat={t('booking.form.dateFormat')}
        dayPlaceholder={t('booking.form.dayPlaceholder')}
        monthPlaceholder={t('booking.form.monthPlaceholder')}
        yearPlaceholder={t('booking.form.yearPlaceholder')}
        informationLabel={t('faceting.who.ageComment')}
        addChildcareLabel={t('faceting.who.addChildcare')}
        birthdaysErrorMessage={
          props.isKidBirthdateRequired ? t('booking.form.errors.birthdayFormat') : ''
        }
        changeKidsNumber={value => {
          setInputKids({ type: KIDS_ACTIONS.CHANGE_KIDS_COUNT, payload: value });
          trackKidsNumberChange(value);
        }}
        changeKid={(index, updatedKid) => {
          setInputKids({
            type: KIDS_ACTIONS.CHANGE_KID,
            payload: {
              index,
              kid: { ...updatedKid, status: IS_VALID },
            },
          });
        }}
        kidsValidationState={inputKidsValidationState}
        validateKidsBirthdate={(index: number, updatedKid) => {
          setInputKids({
            type: KIDS_ACTIONS.CHANGE_KID,
            payload: {
              index,
              kid: {
                ...updatedKid,
                status: IS_INVALID,
              },
            },
          });
        }}
        setAdultsNumber={number => {
          setInputAdultsNumber(number);
          trackAdultsNumberChange(number);
        }}
      />
    </CriteriaTrigger>
  );
};
